import React from 'react';
import styled, { css } from 'styled-components';
import { TitleText } from './Text';
import { FieldProps } from 'formik';
import NumberFormat from 'react-number-format';

interface InputProps {
  label: string;
  type: string;
  name: string;
  placeholder: string;
  required?: boolean;
}

interface StyleProps {
  width?: string;
  height?: string;
  isInline?: boolean;
  backgroundColor?: string;
  color?: string;
}

const Input = ({
  label,
  type,
  placeholder,
  width,
  height,
  isInline,
  disabled,
  required,
  backgroundColor,
  color,
  field: { value, name },
  form: { touched, errors, setFieldValue, setFieldTouched },
}: InputProps & FieldProps & StyleProps & HTMLInputElement) => {
  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { value } = e.target;
    setFieldValue(name, value);
  };
  const handleTouch = () => {
    setFieldTouched(name);
  };
  const errorMessage = touched[name] && errors[name];
  return isInline ? (
    <InLineLabel>
      {label && <TitleText>{label}</TitleText>}
      {required && <Asterisk>*</Asterisk>}
      {type === 'textarea' ? (
        <StyledTextArea
          width={width || '95%'}
          height={height || '0.875em'}
          rows={4}
          value={value}
          placeholder={placeholder}
          onChange={handleTextChange}
          onBlur={handleTouch}
          backgroundColor={backgroundColor}
          color={color}
        />
      ) : (
        <StyledInput
          type={type}
          width={width || '95%'}
          height={height || '0.875em'}
          placeholder={placeholder}
          value={value}
          onChange={handleTextChange}
          onBlur={handleTouch}
          disabled={disabled}
          backgroundColor={backgroundColor}
          color={color}
        />
      )}
      {errorMessage && <ErrorText>{errorMessage}</ErrorText>}
    </InLineLabel>
  ) : (
    <Label>
      {label && <TitleText>{label}</TitleText>}
      {required && <Asterisk>*</Asterisk>}
      {type === 'textarea' ? (
        <StyledTextArea
          width={width || '95%'}
          height={height || '0.875em'}
          rows={4}
          value={value}
          placeholder={placeholder}
          onChange={handleTextChange}
          onBlur={handleTouch}
          backgroundColor={backgroundColor}
          color={color}
        />
      ) : (
        <StyledInput
          type={type}
          width={width || '95%'}
          height={height || '0.875em'}
          placeholder={placeholder}
          value={value}
          onChange={handleTextChange}
          onBlur={handleTouch}
          disabled={disabled}
          backgroundColor={backgroundColor}
          color={color}
        />
      )}
      {errorMessage && <ErrorText>{errorMessage}</ErrorText>}
    </Label>
  );
};
export const NumberInput = (props) => {
  const {
    field: { name, value },
    form: { setFieldValue },
  } = props;

  return (
    <NumberFormat
      customInput={Input}
      placeholder=""
      decimalSeparator="."
      allowNegative= {false}
      thousandSeparator={true}
      value={value}
      onValueChange={(vals) => setFieldValue(name, vals.formattedValue)}
      {...props}
    />
  );
};

export default Input;

const sharedInputStyles = css<StyleProps>`
  padding: 1em;
  padding-left: 1em;
  font-weight: bold;
  color: ${(props) => props.color || '#214160'};
  box-shadow: none;
  border: 1px solid rgba(76, 92, 251, 0.1);
  border-radius: 0.5em;
  display: flex;
  height: ${(props) => props.height};
  align-items: center;
  width: ${(props) => props.width};
  background-color: ${(props) => props.backgroundColor};

  ::placeholder {
    color: #214160;
    opacity: 0.3;
    font-weight: normal;
  }
  &:focus {
    box-shadow: none;
    outline: none;
    -moz-outline-radius: 0.5em;
  }
`;

const StyledInput = styled.input`
  ${sharedInputStyles}
`;

const StyledTextArea = styled.textarea`
  ${sharedInputStyles}
  resize: none;
  overflow: hidden;
  min-height: ${(props) => props.height || '5rem'};
`;

const Label = styled.label`
  display: flex;
  flex-direction: column;
  color: #214160;
  font-family: 'Raleway', sans-serif;
  font-size: 0.75em;
  margin: 0.5em 0;
  position: relative;
  ${TitleText} {
    opacity: 0.4;
    font-weight: normal;
    font-size: 1em;
    padding-bottom: 0.5em;
  }
`;

const ErrorText = styled.p`
  margin: 0;
  color: red;
  font-family: 'Raleway', sans-serif;
`;

const InLineLabel = styled.label`
  display: flex;
  flex-direction: row;
  color: #214160;
  font-family: 'Raleway', sans-serif;
  align-items: center;
  font-size: 0.75em;
  margin: 0.5em 0;
  position: relative;
  ${TitleText} {
    opacity: 0.4;
    font-weight: normal;
    font-size: 1em;
    padding-bottom: 0.5em;
    padding-right: 3em;
  }
`;

const Asterisk = styled.span`
  color: red;
  margin-left: 0.2em;
`;
